'use client';
import { cn } from '@/lib/utils';
import { useScreenSize } from '@/hooks/useScreenSize';

import Heading from '@/components/atoms/Heading';
import ImageStrapi, {
  ImageStrapiProps,
} from '@/components/atoms/Image/ImageStrapi';
import LinkButtonStrapi, {
  LinkButtonStrapiProps,
} from '@/components/atoms/LinkButton/LinkStrapi';
import RichTextFieldStrapi, {
  RichTextFieldStrapiProps,
} from '@/components/atoms/RichTextField/RichTextFieldStrapi';
import {
  ToggleCMSProps,
  useCMSToggle,
} from '@/components/atoms/Toggle/useCMSToggle';

import { BlockHeader, BlockHeaderContent } from '@/cms/BlockHeaderContentDC';
import { BlockWrapper } from '@/cms/BlockWrapperDC';
import { NavPill } from '@/cms/components/page-shell/sidenav/NavPill';
import { BlockComponentModel } from '@/cms/types/blok-component';
import { slugify } from '@/cms/utils';
import {
  FeatureListStrapi,
  FeatureListStrapiProps,
} from '@/features/FeatureListStrapi/FeatureListStrapiDC';
import { SECTION_NAMES } from '@/integrations/trackingScripts/constants';

export interface ImageTextProps {
  image: ImageStrapiProps[];
  header?: BlockHeader;
  toggle?: ToggleCMSProps;
  title: string;
  description?: RichTextFieldStrapiProps;
  buttons?: LinkButtonStrapiProps[];
  layout: 'one-column' | 'two-columns' | 'two-columns-reverse';
  featureList?: FeatureListStrapiProps;
  badge?: string;
}

export function ImageTextStrapi({
  block,
  ...rest
}: BlockComponentModel<ImageTextProps>) {
  const {
    image: images,
    header,
    toggle,
    featureList,
    title,
    description,
    buttons,
    layout = 'two-columns',
    badge,
  } = block;
  const { isDesktop } = useScreenSize();

  const { filteredToggleList, toggleNode: ToggleNode } = useCMSToggle<
    typeof images
  >(images, toggle);

  const withConditionalExpansion = (
    imageBlock: ImageStrapiProps
  ): ImageStrapiProps => {
    return {
      ...imageBlock,
      isExpandable: isDesktop && imageBlock.isExpandable,
    };
  };

  // TODO remove support for single image after migration
  const selectedImages = (
    Array.isArray(images) ? filteredToggleList || images : [images]
  ).map(withConditionalExpansion);

  const layoutClasses = {
    'one-column': 'md:flex-col',
    'two-columns': cn('md:flex-row items-start flex-col'),
    'two-columns-reverse': cn('md:flex-row-reverse items-start flex-col'),
  } as const;

  const slugifiedName = slugify(title ? title : '');

  return (
    <BlockWrapper
      {...rest}
      data-tracker-section={SECTION_NAMES.TEXT_IMAGE}
      classNames={{ root: cn(badge && 'px-9 md:px-0 md:mx-9') }}
    >
      <div>
        {layout !== 'one-column' ? (
          <BlockHeaderContent
            {...header}
            layoutOptions={{
              constrainWidthWithPadding: true,
            }}
            classNames={{
              children: cn(
                layoutClasses[layout],
                'flex-nowrap md:!justify-between'
              ),
            }}
          >
            <div>
              <ImageStrapi
                inner={{
                  classNames: {
                    image:
                      'max-h-[600px] w-full max-w-[440px] rounded md:w-[540px] md:h-[460px]',
                  },
                }}
                block={selectedImages[0]}
              />
            </div>
            <div
              className='flex-1 text-left w-full md:max-w-[648px] md:max-h-[700px]'
              id={slugifiedName}
            >
              {badge && (
                <div className='mb-3'>
                  <NavPill>{badge}</NavPill>
                </div>
              )}
              {title && (
                <Heading level='h3' classNames={{ root: 'text-h2' }}>
                  {title}
                </Heading>
              )}
              {description && (
                <div
                  className='mt-4 text-gray-500 md:mt-6'
                  data-tracker-component='description'
                >
                  <RichTextFieldStrapi
                    block={description}
                    classNames={{ paragraph: 'pb-4' }}
                  />
                </div>
              )}
              {featureList && (
                <div
                  className={cn(
                    featureList.featureLayout === 'column'
                      ? 'flex-col'
                      : 'flex-row',
                    'mt-8 flex flex-wrap  gap-6 max-md:gap-y-4 md:mt-9'
                  )}
                >
                  <FeatureListStrapi
                    features={featureList.features}
                    icon={featureList.icon}
                    classNames={{ item: 'text-body-l-medium' }}
                  />
                </div>
              )}
              {!!buttons?.length && (
                <div
                  className='mt-8 flex gap-4 md:mt-9'
                  data-tracker-component='actions'
                >
                  {buttons.map((button, index) => (
                    <LinkButtonStrapi
                      key={index}
                      block={button}
                      inner={{ classNames: { root: 'w-full md:w-auto' } }}
                    />
                  ))}
                </div>
              )}
            </div>
          </BlockHeaderContent>
        ) : (
          <BlockHeaderContent
            {...header}
            classNames={{
              root: layoutClasses[layout],
            }}
            topChildren={ToggleNode ? ToggleNode() : undefined}
          >
            <ImageStrapi
              inner={{
                classNames: {
                  image: 'rounded',
                },
              }}
              block={selectedImages[0]}
            />
          </BlockHeaderContent>
        )}
      </div>
    </BlockWrapper>
  );
}
